import { Link } from 'react-router-dom';
import { Facebook, Instagram, Mail, MapPin, Phone, Star, Youtube } from 'lucide-react';
import Logo from './Logo';
import { useSiteData } from '../context/SiteDataContext';
import { DAY_KEYS, DAY_LABELS } from '../lib/types';

export default function Footer() {
  const { settings } = useSiteData();
  const socials = [
    { href: settings.socials.instagram, icon: Instagram, label: 'Instagram' },
    { href: settings.socials.facebook, icon: Facebook, label: 'Facebook' },
    { href: settings.socials.youtube, icon: Youtube, label: 'YouTube' },
  ].filter((s) => s.href);

  return (
    <footer className="border-t border-white/[0.06] bg-night-900">
      <div className="container-x grid gap-10 py-14 sm:grid-cols-2 lg:grid-cols-4">
        <div>
          <Logo />
          <p className="mt-4 max-w-xs text-sm leading-relaxed text-zinc-400">{settings.tagline}</p>
          {socials.length > 0 && (
            <div className="mt-4 flex gap-3">
              {socials.map((s) => (
                <a
                  key={s.label}
                  href={s.href}
                  target="_blank"
                  rel="noreferrer"
                  aria-label={s.label}
                  className="glass glass-hover rounded-lg p-2 text-zinc-300"
                >
                  <s.icon size={18} />
                </a>
              ))}
            </div>
          )}
        </div>

        <div>
          <h4 className="font-display font-semibold text-white">Explore</h4>
          <ul className="mt-4 space-y-2.5 text-sm text-zinc-400">
            <li><Link className="transition-colors hover:text-accent-400" to="/services">Detailing Packages</Link></li>
            <li><Link className="transition-colors hover:text-accent-400" to="/custom">Custom Work</Link></li>
            <li><Link className="transition-colors hover:text-accent-400" to="/gallery">Gallery</Link></li>
            <li><Link className="transition-colors hover:text-accent-400" to="/booking">Book an Appointment</Link></li>
            <li><Link className="transition-colors hover:text-accent-400" to="/contact">Contact Us</Link></li>
          </ul>
        </div>

        <div>
          <h4 className="font-display font-semibold text-white">Hours</h4>
          <ul className="mt-4 space-y-1.5 text-sm text-zinc-400">
            {DAY_KEYS.map((d) => {
              const h = settings.hours[d];
              return (
                <li key={d} className="flex justify-between gap-6">
                  <span>{DAY_LABELS[d]}</span>
                  <span className={h?.closed ? 'text-zinc-600' : 'text-zinc-300'}>
                    {h?.closed ? 'Closed' : `${h?.open} – ${h?.close}`}
                  </span>
                </li>
              );
            })}
          </ul>
        </div>

        <div>
          <h4 className="font-display font-semibold text-white">Get In Touch</h4>
          <ul className="mt-4 space-y-3 text-sm text-zinc-400">
            <li>
              <a href={`tel:${settings.phone.replace(/[^0-9+]/g, '')}`} className="flex items-center gap-2.5 transition-colors hover:text-accent-400">
                <Phone size={15} className="text-accent-400" /> {settings.phone}
              </a>
            </li>
            <li>
              <a href={`mailto:${settings.email}`} className="flex items-center gap-2.5 transition-colors hover:text-accent-400">
                <Mail size={15} className="text-accent-400" /> {settings.email}
              </a>
            </li>
            <li className="flex items-center gap-2.5">
              <MapPin size={15} className="shrink-0 text-accent-400" />
              {settings.city} — {settings.serviceArea}
            </li>
            {settings.socials.google && (
              <li>
                <a href={settings.socials.google} target="_blank" rel="noreferrer" className="flex items-center gap-2.5 transition-colors hover:text-accent-400">
                  <Star size={15} className="text-amber-400" /> Review us on Google
                </a>
              </li>
            )}
          </ul>
        </div>
      </div>

      <div className="border-t border-white/[0.06]">
        <div className="container-x flex flex-col items-center justify-between gap-2 py-5 text-xs text-zinc-500 sm:flex-row">
          <span>© {new Date().getFullYear()} {settings.businessName}. All rights reserved.</span>
          <span>apexcarcare.net</span>
        </div>
      </div>
    </footer>
  );
}
